import { AuthContextProvider, BaseApiClient, Provider, ServiceEnvironment } from '@streamelements/client-sdk-js';

export type OverlayTheme = {
  _id: string;
  title: string;
  published: boolean;
  provider: Provider;
  type: 'regular' | 'super' | 'alert';
  size: '1080';
  icon: string;
  thumbnail: string;
  overlays: {
    _id: string;
    overlay: string;
    thumbnail: string;
    preview: string;
  }[];
};

export type Overlay = {
  _id: string;
  name: string;
  theme: string;
  themeId: string;
  settings: {
    width: number;
    height: number;
  };
};

type SourceName = 'camera' | 'screenCapture';
type SourceType = 'ingame' | 'yapping';
type SourceCoordinates = Record<'left' | 'top' | 'width' | 'height', number>;
type CoordinatesRecord = Partial<Record<SourceName, Partial<Record<SourceType, SourceCoordinates>>>>;

export class OverlayThemesApiClient extends BaseApiClient {
  constructor(params: { authContextProvider: AuthContextProvider; serviceEnvironment: ServiceEnvironment }) {
    super({
      authContextProvider: params.authContextProvider,
      baseURL: `${params.serviceEnvironment.kappaV2ApiBaseUrl}/themes`,
    });
  }

  public async getThemeById(params: { themeId: string }) {
    return this.get<OverlayTheme, OverlayTheme, OverlayTheme>(`/${params.themeId}`);
  }

  public static get themesSourcesCoordinates(): Record<string, CoordinatesRecord> {
    return {
      '62d7494f7719ce82785abee0': {
        camera: {
          ingame: {
            left: 75,
            top: 322,
            width: 508,
            height: 282,
          },
          yapping: {
            left: 1370,
            top: 55,
            width: 501,
            height: 282,
          },
        },
        screenCapture: {
          ingame: {
            left: 0,
            top: 0,
            width: 1300,
            height: 728,
          },
        },
      },
      '613b20a06ad45f10ddc8e3b2': {
        camera: {
          ingame: {
            // x: 43px, y: 425px, w: 480px, h: 266px
            left: 43,
            top: 425,
            width: 480,
            height: 266,
          },
          yapping: {
            // x: 1372px, y: 69px, w: 485px, h: 270px
            left: 1372,
            top: 69,
            width: 485,
            height: 270,
          },
        },
        screenCapture: {
          ingame: {
            // x: 68px, y: 69px, w: 1274px, h: 713px
            left: 68,
            top: 69,
            width: 1274,
            height: 713,
          },
        },
      },
      '62d6719a6f9d4e7b9b8b1ef8': {
        camera: {
          ingame: {
            left: 38,
            top: 264,
            width: 558,
            height: 315,
          },
          yapping: {
            left: 1376,
            top: 86,
            width: 488,
            height: 272,
          },
        },
        screenCapture: {
          ingame: {
            left: 80,
            top: 69,
            width: 1258,
            height: 710,
          },
        },
      },
      '5b7058577c02fc4336461d22': {
        camera: {
          ingame: {
            left: 34,
            top: 372,
            width: 450,
            height: 244,
          },
          yapping: {
            // x: 102px, y: 35px, w: 1098px, h: 614px
            left: 102,
            top: 35,
            width: 1098,
            height: 614,
          },
        },
      },
    };
  }

  public async getOnboardingThemesOffer() {
    const themeIds = [
      '62d7494f7719ce82785abee0', // Anime animated
      '613b20a06ad45f10ddc8e3b2', // Connection animated
      '62d6719a6f9d4e7b9b8b1ef8', // Japan traditional animated,
      '5b7058577c02fc4336461d22', // Grey Area
    ];

    const themes = await Promise.all(themeIds.map((themeId) => this.getThemeById({ themeId })));

    return themes;
  }

  public async createOverlaysFromTheme(params: { themeId: string }) {
    const {
      channel: { displayName },
    } = await this.authContextProvider.getAuthContext();
    const overlays = await this.post<void, Overlay[], { overlayName: string }>(`/${params.themeId}`, {
      overlayName: displayName,
    });
    return overlays;
  }
}
